/* eslint-disable react/button-has-type */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */

import { useState, useEffect, useRef } from "react";
import { Search, Plus, Edit2, X, ChevronDown, Check, Tag } from "lucide-react";
import { Snackbar, Alert } from "@mui/material";
import adminServices from '../../../services/adminServices';
import Page from '../../../components/Page'

const EMPTY_B2C_FORM = { code: "", description: "", related_course_category_ids: [], is_active: true };

const Toggle = ({ checked, onChange }) => (
    <button
        type="button"
        role="switch"
        aria-checked={checked}
        onClick={onChange}
        className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${checked ? "bg-blue-500" : "bg-gray-200"}`}
    >
        <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${checked ? "translate-x-4" : "translate-x-0"}`} />
    </button>
);


const ExpandableTags = ({ ids, labelForCategoryId, loadingOptions }) => {
    const [expanded, setExpanded] = useState(false);
    const visible = ids.slice(0, 3);
    const hidden = ids.slice(3);

    return (
        <div className="flex flex-wrap gap-1">
            {visible.map((id) => (
                <span key={id} className="inline-block rounded-md border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-600">
                    {loadingOptions ? "…" : labelForCategoryId(id)}
                </span>
            ))}
            {expanded && hidden.map((id) => (
                <span key={id} className="inline-block rounded-md border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold text-purple-600">
                    {loadingOptions ? "…" : labelForCategoryId(id)}
                </span>
            ))}
            {hidden.length > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded(!expanded)}
                    className="cursor-pointer inline-block rounded-md border border-gray-200 bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-500 hover:bg-gray-200 transition-colors"
                >
                    {expanded ? "show less" : `+${hidden.length} more`}
                </button>
            )}
        </div>
    );
};

const MultiSelect = ({ value, onChange, options, loading, labelId }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (open) inputRef.current?.focus();
    }, [open]);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) {
                setOpen(false);
                setSearch("");
            }
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const toggle = (id) => {
        if (value.includes(id)) onChange(value.filter((v) => v !== id));
        else onChange([...value, id]);
    };

    const filtered = options.filter((o) =>
        o.code?.toLowerCase().includes(search.toLowerCase())
    );

    // Show max 3 tags in trigger, rest as count badge
    const MAX_VISIBLE = 3;
    const visibleTags = value.slice(0, MAX_VISIBLE);
    const extraCount = value.length - MAX_VISIBLE;

    return (
        <div ref={ref} className="relative">
            {/* Trigger button — capped height, no overflow */}
            <button
                type="button"
                aria-labelledby={labelId}
                aria-haspopup="listbox"
                aria-expanded={open}
                onClick={() => setOpen(!open)}
                className="w-full min-h-[38px] max-h-[72px] overflow-hidden rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 flex items-center gap-1 flex-wrap"
            >
                {value.length === 0 ? (
                    <span className="text-gray-400 text-sm py-0.5">Select categories…</span>
                ) : (
                    <>
                        {visibleTags.map((v) => (
                            <span
                                key={v}
                                className="inline-flex items-center gap-1 rounded-md bg-blue-50 border border-blue-200 px-2 py-0.5 text-xs font-semibold text-blue-600"
                            >
                                {options.find(o => o.id === v)?.code ?? String(v)}
                                <button
                                    type="button"
                                    onMouseDown={(e) => { e.stopPropagation(); toggle(v); }}
                                    className="hover:text-blue-900"
                                >
                                    <X size={10} />
                                </button>
                            </span>
                        ))}
                        {extraCount > 0 && (
                            <span className="inline-flex items-center rounded-md bg-gray-100 border border-gray-200 px-2 py-0.5 text-xs font-semibold text-gray-500">
                                +{extraCount} more
                            </span>
                        )}
                    </>
                )}
                <ChevronDown
                    size={14}
                    className={`ml-auto shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <div className="absolute z-[9999] mt-1 w-full rounded-xl border border-gray-200 bg-white shadow-xl max-h-60 overflow-y-auto">
                    {/* Selected count + clear all bar */}
                    {value.length > 0 && (
                        <div className="sticky top-0 bg-blue-50 border-b border-blue-100 px-3 py-1.5 flex items-center justify-between">
                            <span className="text-xs font-semibold text-blue-600">
                                {value.length} selected
                            </span>
                            <button
                                type="button"
                                onClick={() => onChange([])}
                                className="text-xs text-red-400 hover:text-red-600 font-medium transition-colors"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                    {/* Search */}
                    <div className="sticky top-0 bg-white px-2 pt-2 pb-1 border-b border-gray-100">
                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5 focus-within:border-blue-400 focus-within:ring-1 focus-within:ring-blue-200">
                            <Search size={13} className="shrink-0 text-gray-400" />
                            <input
                                ref={inputRef}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search…"
                                className="w-full text-xs bg-transparent focus:outline-none text-gray-700 placeholder-gray-400"
                            />
                            {search && (
                                <button type="button" onClick={() => setSearch("")} className="text-gray-400 hover:text-gray-600">
                                    <X size={11} />
                                </button>
                            )}
                        </div>
                    </div>
                    {loading && <div className="px-3 py-4 text-center text-sm text-gray-400">Loading…</div>}
                    {!loading && filtered.length === 0 && <div className="px-3 py-4 text-center text-sm text-gray-400">No options found</div>}
                    {!loading && filtered.length > 0 && filtered.map((opt) => (
                        <button
                            key={opt.id}
                            type="button"
                            onClick={() => toggle(opt.id)}
                            className="flex w-full items-center gap-2.5 px-3 py-2 text-sm hover:bg-gray-50 transition-colors"
                        >
                            <div className={`h-4 w-4 shrink-0 rounded flex items-center justify-center border transition-colors ${value.map(Number).includes(Number(opt.id)) ? "bg-blue-500 border-blue-500" : "border-gray-300"}`}>
                                {value.map(Number).includes(Number(opt.id)) && <Check size={10} className="text-white" strokeWidth={3} />}
                            </div>
                            <span className="text-gray-700">{opt.code}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

// ─── Delete Dialog 
const DeleteDialog = ({ open, item, onConfirm, onCancel, nameKey = "description" }) => {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-sm rounded-2xl bg-white shadow-2xl">
                <div className="px-6 py-4">
                    <h3 className="text-base font-bold text-gray-900">Delete Item</h3>
                </div>
                <div className="h-px bg-gray-100" />
                <div className="px-6 py-4">
                    <p className="text-sm text-gray-500 leading-relaxed">
                        Are you sure you want to delete{" "}
                        <span className="font-bold text-gray-800">{item?.[nameKey]}</span>? This action cannot be undone.
                    </p>
                </div>
                <div className="h-px bg-gray-100" />
                <div className="flex justify-end gap-2 px-6 py-4">
                    <button onClick={onCancel} className="rounded-lg border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button onClick={onConfirm} className="rounded-lg bg-red-500 px-4 py-1.5 text-sm font-semibold text-white hover:bg-red-600 transition-colors">
                        Delete
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Category Modal (course_b2c_category_lk) 
const getRelatedIds = (item) => {
    if (item.related_course_category_id) {
        return Array.isArray(item.related_course_category_id)
            ? item.related_course_category_id.map(Number)
            : [Number(item.related_course_category_id)];
    }
    if (item.related_course_category_ids) {
        return Array.isArray(item.related_course_category_ids)
            ? item.related_course_category_ids.map(Number)
            : [Number(item.related_course_category_ids)];
    }
    return [];
};

const B2CCategoryModal = ({ open, editItem, onClose, onSave, categoryOptions, loadingOptions, existingRows = [] }) => {
    const isEdit = Boolean(editItem);
    const [form, setForm] = useState(EMPTY_B2C_FORM);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (editItem) {
            setForm({
                code: (editItem.name || "").toUpperCase(),
                description: editItem.description || "",
                related_course_category_ids: getRelatedIds(editItem),
                is_active: editItem.is_active ?? true,
            });
        } else {
            setForm(EMPTY_B2C_FORM);
        }
        setErrors({});
    }, [editItem, open]);


const checkDuplicates = (code, description) => {
    const normalizedCode = code.trim().toUpperCase();
    const normalizedDesc = description.trim().toLowerCase();

    return existingRows.reduce((dupErrors, r) => {
        const isSameRow = editItem && Number(r.id) === Number(editItem.id);
        if (isSameRow) return dupErrors;

        if (normalizedCode && (r.name ?? r.code ?? "").trim().toUpperCase() === normalizedCode)
            dupErrors.code = `Code "${code.trim()}" is already taken`;

        if (normalizedDesc && (r.description ?? "").trim().toLowerCase() === normalizedDesc)
            dupErrors.description = `Description "${description.trim()}" is already used by another category`;

        return dupErrors;
    }, {});
};
const validate = () => {
    const e = {};
    if (!form.code?.trim()) e.code = "Category name is required";
    if (!form.description?.trim()) e.description = "Description is required";
    if (!form.related_course_category_ids?.length) e.related_course_category_ids = "Select at least one related category";
    if (typeof form.is_active !== "boolean") e.is_active = "Status is required";
    if (!e.code && !e.description) {
    const dupErrors = checkDuplicates(form.code, form.description);
    Object.assign(e, dupErrors);
}
    return e;
};

    useEffect(() => {
        if (open) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [open]);


    const handleSave = async () => {
        const e = validate();
        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }
        try {
            setSaving(true);
            await onSave(form);
        } catch (err) {
            const msg = err?.message || '';
            if (err?.statusCode === 400 && msg.toLowerCase().includes('already exists')) {
                setErrors({ code: msg });
            }
        } finally {
            setSaving(false);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl flex flex-col max-h-[86vh] mt-14">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
                            <Tag size={18} className="text-white" />
                        </div>
                        <div>
                            <p className="text-[15px] font-bold leading-tight text-gray-900">
                                {isEdit ? "Edit B2C Category" : "Add New B2C Category"}
                            </p>
                            <p className="text-xs text-gray-400 mt-0.5">
                                {isEdit ? `Editing: ${editItem?.name}` : "Fill in the details below"}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors">
                        <X size={16} />
                    </button>
                </div>

                <div className="h-px bg-gray-100 shrink-0" />

                {/* Body — scrollable so dropdown can overflow freely */}
                <div className="flex flex-col gap-4 px-6 py-5 overflow-y-auto flex-1">

                    {/* Code */}
                    <div>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor="b2c-code" className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Code <span className="text-red-600">*</span>
                        </label>
                        <input
                            id="b2c-code"
                            value={form.code}
                            onChange={(e) => {
                                const newCode = e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "");
                                setForm({ ...form, code: newCode });
                                const dupErrors = checkDuplicates(newCode, form.description);
                                setErrors((prev) => ({ ...prev, code: dupErrors.code ?? null }));
                            }}
                            placeholder="e.g. ADVANCED_EXCEL"
                            className={`w-full rounded-lg border px-3 py-2 font-mono text-sm tracking-wide focus:outline-none focus:ring-2 focus:ring-purple-500 transition-colors ${errors.code ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                        />
                        {errors.code
                            ? <p className="mt-1 text-xs text-red-500">{errors.code}</p>
                            : <p className="mt-1 text-xs text-gray-400">Only uppercase letters, numbers, and underscores</p>
                        }
                    </div>

                    {/* Description */}
                    <div>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label htmlFor="b2c-description" className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Description <span className="text-red-600">*</span>
                        </label>
                        <input
                            id="b2c-description"
                            value={form.description}
                            onChange={(e) => {
                                const newDesc = e.target.value;
                                setForm({ ...form, description: newDesc });
                                const dupErrors = checkDuplicates(form.code, newDesc);
                                setErrors((prev) => ({ ...prev, description: dupErrors.description ?? null }));
                            }}
                            className={`w-full rounded-lg border px-3 py-2 text-sm ${errors.description ? "border-red-400 bg-red-50" : "border-gray-300"}`}
                        />
                        {errors.description && (
                            <p className="mt-1 text-xs text-red-500">{errors.description}</p>
                        )}
                    </div>

                    {/* Related Course Category */}
                    <div>
                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label className="mb-1.5 block text-xs font-semibold text-gray-600 uppercase tracking-wide">
                            Related Course Category <span className="text-red-600">*</span>
                        </label>
                        <MultiSelect
                            labelId="b2c-related"
                            value={form.related_course_category_ids}
                            onChange={(v) => {
                                setForm({ ...form, related_course_category_ids: v });
                                setErrors({ ...errors, related_course_category_ids: null });
                            }}
                            options={categoryOptions}
                            loading={loadingOptions}
                        />
                        {errors.related_course_category_ids && (
                            <p className="mt-1 text-xs text-red-500">{errors.related_course_category_ids}</p>
                        )}
                    </div>

                    {/* Is Active */}
                    <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-purple-50/40 px-4 py-3">
                        <div>
                            <p className="text-sm font-semibold text-gray-800">Is Active</p>
                            <p className="text-xs text-gray-400 mt-0.5">Inactive categories won't appear in listings</p>
                        </div>
                        <Toggle checked={form.is_active} onChange={() => setForm({ ...form, is_active: !form.is_active })} />
                    </div>
                </div>

                <div className="h-px bg-gray-100 shrink-0" />

                {/* Footer */}
                <div className="flex justify-end gap-2 px-6 py-4 shrink-0">
                    <button onClick={onClose} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`flex items-center justify-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold text-white shadow-md transition-all
                        ${saving
                                ? "cursor-not-allowed bg-gray-400 shadow-none"
                                : "bg-gradient-to-r from-blue-500 to-blue-600 shadow-blue-200 hover:from-blue-600 hover:to-blue-700"
                            }`}
                    >
                        {saving && (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        )}
                        {saving ? "Saving..." : isEdit ? "Save Changes" : "Create B2C Category"}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── B2C Category CRUD (course_b2c_category_lk) 
const B2CCategoryTab = ({ categoryOptions, loadingOptions }) => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [modal, setModal] = useState({ open: false, editItem: null });
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [draggedId, setDraggedId] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const scrollRafRef = useRef(null);

    useEffect(() => {
        fetchB2CCategories();
    }, []);

    const fetchB2CCategories = async () => {
        setLoading(true);
        try {
            const res = await adminServices.getB2CCategoryList();
            if (res.ok && res.data?.data) {
                setRows(res.data.data);
            }
        } catch (err) {
            console.error('Failed to load B2C categories:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleDragStart = (e, row) => {
        setDraggedId(row.id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e, row) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (row.id !== draggedId) {
            setDragOverId(row.id);
        }

        const ZONE = 120;   // px from edge that triggers scroll
        const SPEED = 12;   // px per frame
        const y = e.clientY;
        const vh = window.innerHeight;

        if (scrollRafRef.current) {
            cancelAnimationFrame(scrollRafRef.current);
            scrollRafRef.current = null;
        }

        const scroll = () => {
            if (y < ZONE) {
                window.scrollBy(0, -SPEED);
                scrollRafRef.current = requestAnimationFrame(scroll);
            } else if (y > vh - ZONE) {
                window.scrollBy(0, SPEED);
                scrollRafRef.current = requestAnimationFrame(scroll);
            }
        };
        scroll();
    };

    const handleDrop = async (e, targetRow) => {
        e.preventDefault();
        setDragOverId(null);

        if (!draggedId || draggedId === targetRow.id) {
            setDraggedId(null);
            return;
        }

        const draggedIndex = rows.findIndex(r => r.id === draggedId);
        const targetIndex = rows.findIndex(r => r.id === targetRow.id);

        // Save previous state for rollback
        const previousRows = [...rows];

        // Reorder locally
        const reordered = [...rows];
        const [removed] = reordered.splice(draggedIndex, 1);
        reordered.splice(targetIndex, 0, removed);

        const updated = reordered.map((r, i) => ({ ...r, sort_order: i + 1 }));

        // Optimistic update
        setRows(updated);
        setDraggedId(null);

        // Only send changed rows
        const originalOrderMap = previousRows.reduce((acc, r, i) => {
            acc[r.id] = i + 1;
            return acc;
        }, {});

        const changes = updated
            .filter(r => r.sort_order !== originalOrderMap[r.id])
            .map(r => ({ id: r.id, sort_order: r.sort_order }));

        try {
            const res = await adminServices.updateB2CSortOrder({ updates: changes });
            if (!res.ok) {
                setRows(previousRows); // ← rollback to saved previous
                setSnackbar({ open: true, message: 'Failed to update order', severity: 'error' });
            }
            //  on success — do nothing, optimistic update already shows correct order
        } catch (err) {
            setRows(previousRows); // ← rollback to saved previous
            setSnackbar({ open: true, message: 'Failed to update order', severity: 'error' });
        }
    };

    const handleDragEnd = () => {
        setDraggedId(null);
        setDragOverId(null);
        if (scrollRafRef.current) {
            cancelAnimationFrame(scrollRafRef.current);
            scrollRafRef.current = null;
        }
    };

    const handleSave = async (form) => {
        try {
          let res;
            if (modal.editItem) {
            res = await adminServices.updateB2CCategory(form, modal.editItem.id);
            } else {
            res = await adminServices.createB2CCategory(form);
        }
        if (!res.ok) {
            const err = new Error(res.data?.message || 'Failed to save B2C category');
            err.statusCode = res.status;
            throw err;
            }
            await fetchB2CCategories();
            setModal({ open: false, editItem: null });
            setSnackbar({ open: true, message: modal.editItem ? 'Category updated successfully' : 'Category created successfully', severity: 'success' });
        } catch (err) {
            const message = err?.message || '';
            // Duplicate errors surface inline in the modal — don't double-show as snackbar
            const isDuplicateError = err?.statusCode === 400 && message.toLowerCase().includes('already exists');
            if (!isDuplicateError) {
                setSnackbar({ open: true, message: message || 'Failed to save B2C category', severity: 'error' });
            }
            console.error('Failed to save B2C category:', err);
            throw err; 
        }
    };

    const confirmDelete = async () => {
        try {
            await adminServices.deleteB2CCategory(deleteDialog.item?.id);
            await fetchB2CCategories();
            setDeleteDialog({ open: false, item: null });
        } catch (err) {
            console.error('Failed to delete B2C category:', err);
        }
    };

    const labelForCategoryId = (id) => {
        const match = categoryOptions.find((o) => Number(o.id) === Number(id));
        return match ? (match.code || match.description) : String(id);
    };

    const filtered = rows.filter(
        (r) =>
            r.name?.toLowerCase().includes(search.toLowerCase()) ||
            r.description?.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <>
            {/* Header */}
            <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2.5 mb-1">
                        <Tag size={22} className="text-purple-500" />
                        <h2 className="text-xl font-extrabold tracking-tight text-gray-900">B2C Categories</h2>
                    </div>
                    <p className="text-sm text-gray-500">Manage B2C course categories</p>
                </div>
                <button
                    onClick={() => setModal({ open: true, editItem: null })}
                    className="flex items-center gap-2 rounded-xl bg-[rgb(37,99,235)] px-4 py-2.5 text-sm font-bold text-white shadow-lg shadow-blue-200 hover:bg-[rgb(29,78,216)] transition-all"
                >
                    <Plus size={17} />
                    Add B2C Category
                </button>
            </div>

            {/* Stats + Search */}
            <div className="mb-4 flex flex-wrap items-center gap-2">
                {[
                    { label: "Total", value: rows.length, bg: "bg-purple-50", border: "border-purple-100", text: "text-purple-500" },
                    { label: "Active", value: rows.filter((r) => r.is_active).length, bg: "bg-green-50", border: "border-green-100", text: "text-green-500" },
                    { label: "Inactive", value: rows.filter((r) => !r.is_active).length, bg: "bg-red-50", border: "border-red-100", text: "text-red-400" },
                ].map(({ label, value, bg, border, text }) => (
                    <div key={label} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${bg} ${border}`}>
                        <span className={`text-xl font-extrabold leading-none ${text}`}>{value}</span>
                        <span className="text-xs font-semibold text-gray-500">{label}</span>
                    </div>
                ))}
                <div className="ml-auto flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 shadow-sm focus-within:border-purple-400 focus-within:ring-1 focus-within:ring-purple-200 transition-all">
                    <Search size={15} className="shrink-0 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search B2C categories…"
                        className="w-52 text-sm bg-transparent focus:outline-none text-gray-700 placeholder-gray-400"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-visible">
                <div className="overflow-x-auto relative">
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="bg-gray-50 border-b-2 border-gray-200">
                                {["ID", "Name", "Description", "Related Category", "Is Active", "Actions"].map((h) => (
                                    <th key={h} className={`py-3.5 px-4 text-xs font-bold uppercase tracking-widest text-gray-500 whitespace-nowrap ${h === "Actions" ? "text-center" : ""}`}>
                                        {h}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center text-sm text-gray-400">Loading…</td>
                                </tr>
                            )}
                            {!loading && filtered.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="py-16 text-center text-sm text-gray-400">No B2C categories found</td>
                                </tr>
                            )}
                            {!loading && filtered.length > 0 && filtered.map((row, index) => (
                                <tr
                                    key={row.id}
                                    draggable={!search}
                                    onDragStart={(e) => handleDragStart(e, row)}
                                    onDragOver={(e) => handleDragOver(e, row)}
                                    onDrop={(e) => handleDrop(e, row)}
                                    onDragEnd={handleDragEnd}
                                    className={`border-b border-gray-100 last:border-0 transition-colors
                                               ${dragOverId === row.id ? 'bg-blue-50 border-t-2 border-t-blue-400' : 'hover:bg-purple-50/30'}
                                               ${draggedId === row.id ? 'opacity-40' : 'opacity-100'}
                                               ${!search ? 'cursor-grab' : ''}`}>
                                    <td className="px-4 py-3.5 w-16">
                                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-purple-50 text-xs font-bold text-purple-500">{index + 1}</div>
                                    </td>
                                    <td className="px-4 py-3.5">
                                        <span className="text-sm font-semibold text-gray-800">{row.name}</span>
                                    </td>
                                    <td className="px-4 py-3.5">
                                        <span className="text-sm text-gray-600">{row.description || <span className="text-gray-300 italic">—</span>}</span>
                                    </td>
                                    <td className="px-4 py-3.5 max-w-xs">
                                        {Array.isArray(row.related_course_category_id) && row.related_course_category_id.length > 0 ? (
                                            <ExpandableTags
                                                ids={row.related_course_category_id}
                                                labelForCategoryId={labelForCategoryId}
                                                loadingOptions={loadingOptions}
                                            />
                                        ) : (
                                            <span className="text-gray-300 italic text-xs">—</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3.5">
                                        <div className="flex items-center gap-2">
                                            <span className={`inline-block rounded-full px-2.5 py-0.5 text-[11px] font-bold ${row.is_active ? "bg-green-50 text-green-600" : "bg-red-50 text-red-400"}`}>
                                                {row.is_active ? "Active" : "Inactive"}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3.5">
                                        <div className="flex justify-center gap-1.5 items-center">
                                            <button onClick={() => setModal({ open: true, editItem: row })} className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-50 text-purple-500 hover:bg-purple-100 transition-colors">
                                                <Edit2 size={14} />
                                            </button>
                                            {!search && (
                                                <div className="flex flex-col gap-0.5 items-center justify-center cursor-grab text-gray-300 hover:text-gray-500 transition-colors px-1">
                                                    <span className="block w-4 h-0.5 bg-current rounded" />
                                                    <span className="block w-4 h-0.5 bg-current rounded" />
                                                    <span className="block w-4 h-0.5 bg-current rounded" />
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-3">
                    <p className="text-xs text-gray-400">
                        Showing <span className="font-semibold text-gray-600">{filtered.length}</span> of{" "}
                        <span className="font-semibold text-gray-600">{rows.length}</span> B2C categories
                    </p>
                </div>
            </div>

            <B2CCategoryModal
                open={modal.open}
                editItem={modal.editItem}
                onClose={() => setModal({ open: false, editItem: null })}
                onSave={handleSave}
                categoryOptions={categoryOptions}
                loadingOptions={loadingOptions}
                existingRows={rows}
            />
            <DeleteDialog
                open={deleteDialog.open}
                item={deleteDialog.item}
                onConfirm={confirmDelete}
                onCancel={() => setDeleteDialog({ open: false, item: null })}
                nameKey="name"
            />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{
                        backgroundColor: "#4B5563",
                        color: "#fff",
                        "& .MuiAlert-icon": { color: "#fff" },
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

// ─── Main Component 
const CategorySettings = () => {
    const [courseCategory, setCourseCategory] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);

    useEffect(() => {
        fetchCourseCategory();
    }, []);

    const fetchCourseCategory = async () => {
        setLoadingOptions(true);
        try {
            const res = await adminServices.getCourseCategoryDropdown();
            if (res.ok && res.data?.data) {
                setCourseCategory(res.data.data);
            }
        } catch (err) {
            console.error('Failed to load sub-category options:', err);
        } finally {
            setLoadingOptions(false);
        }
    };

    return (
        <Page title="">
            <div className="mt-4 p-6">
                <B2CCategoryTab
                    categoryOptions={courseCategory}
                    loadingOptions={loadingOptions}
                />
            </div>
        </Page>
    );
};

export default CategorySettings;